import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Header from "../components/Header";
import { BookOpen, Clock, Cloud } from "lucide-react";

const Welcome = () => {
 const { user, registerUser, loading } = useUser();
 const navigate = useNavigate();
 const [name, setName] = useState(() => {
 try {
 const params = new URLSearchParams(window.location.search);
 return params.get("learner_name") || "";
 } catch {
 return "";
 }
 });
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState("");

 React.useEffect(() => {
 if (user) navigate("/home");
 }, [user, navigate]);

 const submit = async (e) => {
 e.preventDefault();
 const trimmed = name.trim();
 if (!trimmed) {
 setError("Please enter your full name.");
 return;
 }
 if (trimmed.length > 80) {
 setError("Name is too long (max 80 characters).");
 return;
 }
 setSubmitting(true);
 setError("");
 try {
 await registerUser(trimmed);
 navigate("/home");
 } catch (err) {
 setError(err?.response?.data?.detail || "Failed to save name. Please try again.");
 } finally {
 setSubmitting(false);
 }
 };

 if (loading || user) {
 return (
 <div className="min-h-screen bg-white flex flex-col items-center justify-center">
 <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
 <p className="text-zinc-500 text-sm">
 {user ? "Redirecting..." : "Loading module..."}
 </p>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-white ">
 <Header showNav={false} />

 <main className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
 <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
 {/* Left — Intro */}
 <div>
 <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-orange-500/10 text-orange-700 mb-5 border border-orange-500/20">
 <Cloud className="w-4 h-4" />
 <span className="text-xs uppercase tracking-[0.2em] font-semibold">
 Steadfast IT Consulting · eLearning
 </span>
 </div>

 <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-zinc-900 leading-[1.05]">
 Cloud Computing
 <span className="block text-orange-500">Foundations</span>
 </h1>

 <p className="mt-5 text-base md:text-lg text-zinc-600 max-w-xl leading-relaxed">
 A 40-hour, non-technical journey through the cloud — from what it really is, to
 the services behind AWS, Azure, and GCP, and finally to certification readiness.
 Built for university students, explained in plain language.
 </p>

 <div className="mt-6 flex items-center gap-6 text-sm text-zinc-600 ">
 <div className="flex items-center gap-2">
 <BookOpen className="w-4 h-4 text-orange-500" />
 <span>10 Modules</span>
 </div>
 <div className="flex items-center gap-2">
 <Clock className="w-4 h-4 text-orange-500" />
 <span>40 hours</span>
 </div>
 </div>
 </div>

 {/* Right — Name capture card */}
 <div className="bg-white border border-zinc-200 rounded-sm p-8 md:p-10 shadow-none">
 <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-600 mb-2">
 Step 1 of 1
 </div>
 <h2 className="font-heading text-2xl md:text-3xl font-bold text-zinc-900 mb-2">
 Let&apos;s start with your name
 </h2>
 <p className="text-sm md:text-base text-zinc-600 mb-6">
 This is the name that will appear on your certificate of completion, so spell it
 exactly the way you want it printed. You will not be able to change it later.
 </p>

 <form onSubmit={submit} className="space-y-5">
 <div>
 <label
 htmlFor="name"
 className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-700 mb-2"
 >
 Full name
 </label>
 <Input
 id="name"
 data-testid="welcome-name-input"
 type="text"
 placeholder="e.g. Priya Sharma"
 value={name}
 onChange={(e) => setName(e.target.value)}
 maxLength={80}
 autoFocus
 className="rounded-sm border-zinc-300 h-12 text-base focus-visible:ring-orange-500 focus-visible:border-orange-500"
 />
 {error && (
 <p
 data-testid="welcome-error"
 className="mt-2 text-sm text-red-600 "
 >
 {error}
 </p>
 )}
 </div>

 <Button
 type="submit"
 data-testid="submit-name-btn"
 disabled={submitting}
 className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 text-black font-bold h-12 text-base"
 >
 {submitting ? "Starting…" : "Start my training →"}
 </Button>

 <p className="text-xs text-zinc-500 ">
 By continuing, you acknowledge that the name above will be printed on the
 final certificate. If you spot a typo later, contact{" "}
 <span className="font-semibold text-zinc-700 ">
 operations@steadfastitconsulting.in
 </span>
 .
 </p>
 </form>
 </div>
 </div>
 </main>
 </div>
 );
};

export default Welcome;
